import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CollectionMeta,
  CreateTaskInput,
  Task,
  TaskCounts,
  TaskListQuery,
  UpdateTaskInput,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const KEY = ['tasks'] as const;

export interface TaskPage {
  items: Task[];
  meta: CollectionMeta;
}

function toQuery(q: TaskListQuery): Record<string, string | number | boolean | undefined> {
  return {
    page: q.page,
    pageSize: q.pageSize,
    search: q.search || undefined,
    related: q.related || undefined,
    status: q.status,
    priority: q.priority,
    contactId: q.contactId,
    leadId: q.leadId,
    conversationId: q.conversationId,
    assignedUserId: q.assignedUserId,
    mine: q.mine ? 'true' : undefined,
    dueBefore: q.dueBefore,
    dueAfter: q.dueAfter,
    sortBy: q.sortBy,
    sortOrder: q.sortOrder,
  };
}

export async function listTasks(q: TaskListQuery): Promise<TaskPage> {
  const res = await apiClient.getCollection<Task>('/tasks', { query: toQuery(q) });
  return { items: res.data, meta: res.meta };
}
export const getTask = (id: string) => apiClient.get<Task>(`/tasks/${id}`);
export const createTask = (input: CreateTaskInput) => apiClient.post<Task>('/tasks', input);
export const updateTask = (id: string, patch: UpdateTaskInput) =>
  apiClient.patch<Task>(`/tasks/${id}`, patch);
export const deleteTask = (id: string) => apiClient.delete<void>(`/tasks/${id}`);

export function useTasks(query: TaskListQuery) {
  return useQuery<TaskPage, ApiClientError>({
    queryKey: [...KEY, 'list', query],
    queryFn: () => listTasks(query),
    placeholderData: keepPreviousData,
  });
}

/** All / per-status counts for the grid tabs, under the same filters (status ignored). */
export function useTaskCounts(query: TaskListQuery) {
  const { page: _p, pageSize: _ps, sortBy: _s, sortOrder: _o, status: _st, ...filters } = query;
  return useQuery<TaskCounts, ApiClientError>({
    queryKey: [...KEY, 'counts', filters],
    queryFn: () => apiClient.get<TaskCounts>('/tasks/counts', { query: toQuery(filters) }),
    placeholderData: keepPreviousData,
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation<Task, ApiClientError, CreateTaskInput>({
    mutationFn: createTask,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation<Task, ApiClientError, { id: string; patch: UpdateTaskInput }>({
    mutationFn: ({ id, patch }) => updateTask(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: deleteTask,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
